import { registerText } from "../../../model/invoice-document";
import {
  DELIVERABLE_FORMATS, DELIVERABLE_FORMAT_PLATFORM, addMonths, deliverableLabel, exclusivityConflicts, followUpDue, footerLinesOf, isOpenStage, lastDayOfMonth,
  monthsBetween, offerLineAmounts, parseAmountToCents, offerParties, offerTotals, openOfferValue, priceBasisText, surchargesFromCard, surchargesFromJson,
  type Currency, type InquiryStage, type Language, type MoneyLocale, type OfferPdfModel, type RateCard, type SnapshotParty, type Surcharge,
  ORIGIN_LABEL, type DealOrigin,
} from "@talentkontor/domain";
import type { Pill } from "../../../model/invoice-view";
import { pill } from "../kooperationen/model";
import type { AnfrageDetail, AnfragenDaten, AngebotsZeile, CandidateRow, CandidateStatus, InquiryRow, OfferLineRow, OfferRow, RateCardRow, RateItemRow } from "./daten";
import { currentLang, t } from "../../../lib/sprache";
import { parseQuantityInput } from "../../../lib/betrag";

/**
 * Anfragen und Angebote (0072): reine Logik der Seiten. Stände, Liste und Board, Formular der Anfrage, Exklusivität, Angebotszeilen
 * mit Zuschlägen (Rechenregel aus packages/domain/src/offers.ts), Vorbelegung aus der Preisliste und das Modell des PDF.
 */

export const STAGE_ORDER: readonly InquiryStage[] = ["inquiry", "offer_sent", "negotiation", "won", "declined", "lost"];
export const STAGE_PILL: Readonly<Record<InquiryStage, Pill>> = {
  get inquiry() { return pill(t("Anfrage", "Inquiry"), "info"); },
  get offer_sent() { return pill(t("Angebot gesendet", "Offer sent"), "warning"); },
  get negotiation() { return pill(t("Verhandlung", "Negotiation"), "warning"); },
  get won() { return pill(t("Zusage", "Accepted"), "success"); },
  get declined() { return pill(t("Abgesagt", "Declined"), "neutral"); },
  get lost() { return pill(t("Verloren", "Lost"), "neutral"); },
};
export const CANDIDATE_PILL: Readonly<Record<CandidateStatus, Pill>> = {
  get proposed() { return pill(t("Vorgeschlagen", "Proposed"), "info"); },
  get accepted() { return pill(t("Zusage", "Accepted"), "success"); },
  get declined() { return pill(t("Abgesagt", "Declined"), "neutral"); },
  get lost() { return pill(t("Verloren", "Lost"), "neutral"); },
};
export const OFFER_PILL: Readonly<Record<OfferRow["status"], Pill>> = {
  get draft() { return pill(t("Entwurf", "Draft"), "neutral"); },
  get final() { return pill(t("Festgeschrieben", "Finalised"), "info"); },
  get sent() { return pill(t("Gesendet", "Sent"), "success"); },
  get superseded() { return pill(t("Überholt", "Superseded"), "neutral"); },
};
export const SOURCE_LABEL: Readonly<Record<string, string>> = {
  get email() { return t("E-Mail", "Email"); }, get phone() { return t("Telefon", "Phone"); }, get form() { return t("Formular / Website", "Form / website"); }, event: "Event / Messe", get agency() { return t("Über eine Agentur", "Via an agency"); }, get other() { return t("Sonstiges", "Other"); },
};

export type Talentlike = { id: string; first_name: string; last_name: string | null; display_name: string | null; status?: string };
export function nameOf(t: Talentlike | undefined): string {
  if (!t) return "Creator";
  return t.display_name?.trim() || `${t.first_name} ${t.last_name ?? ""}`.trim();
}

// ── Liste und Board ─────────────────────────────────────────────────────────

export interface AnfrageZeile {
  inquiry: InquiryRow;
  brandName: string;
  candidates: CandidateRow[];
  creator: string[];
  managerName: string | null;
  /** zuletzt gesendetes, sonst festgeschriebenes Angebot */
  angebot: AnfragenDaten["offers"][number] | null;
  /** Wert des Angebots (Gesamtsumme), null ohne Angebot oder ohne Recht „Beträge sehen“ */
  wertCents: number | null;
  nachfassen: boolean;
  offen: boolean;
}

/** Maßgebliche Fassung einer Anfrage: die zuletzt gesendete, sonst die zuletzt festgeschriebene (wie inquiry_candidate_accept) */
export function massgeblichesAngebot<T extends { status: string; version: number }>(offers: readonly T[]): T | null {
  const neueste = (status: string) => offers.filter((o) => o.status === status).sort((a, b) => b.version - a.version)[0];
  return neueste("sent") ?? neueste("final") ?? null;
}

export function baueZeilen(d: AnfragenDaten, ctx: { today: string; talents: readonly Talentlike[]; brands: readonly { id: string; name: string }[] }): AnfrageZeile[] {
  const talentName = (id: string) => nameOf(ctx.talents.find((t) => t.id === id));
  const followDays = d.settings?.follow_up_days ?? 5;
  return d.inquiries.map((i) => {
    const candidates = d.candidates.filter((c) => c.inquiry_id === i.id);
    const offers = d.offers.filter((o) => o.inquiry_id === i.id);
    const sent = offers.filter((o) => o.status === "sent").sort((a, b) => b.version - a.version)[0];
    const angebot = massgeblichesAngebot(offers);
    return {
      inquiry: i,
      brandName: ctx.brands.find((b) => b.id === i.brand_id)?.name ?? "–",
      candidates,
      creator: candidates.map((c) => talentName(c.talent_id)),
      managerName: d.members.find((m) => m.user_id === i.manager_id)?.name ?? null,
      angebot,
      wertCents: angebot ? angebot.total_cents : null,
      nachfassen: followUpDue(i.stage, sent?.sent_at?.slice(0, 10) ?? null, followDays, ctx.today),
      offen: isOpenStage(i.stage),
    };
  });
}

export type AnfrageFilter = "offen" | InquiryStage | "alle";
export const FILTERS: readonly { key: AnfrageFilter; label: string }[] = [
  { key: "offen", get label() { return t("Offen", "Open"); } },
  { key: "inquiry", get label() { return t("Anfrage", "Inquiry"); } },
  { key: "offer_sent", get label() { return t("Angebot gesendet", "Offer sent"); } },
  { key: "negotiation", get label() { return t("Verhandlung", "Negotiation"); } },
  { key: "won", get label() { return t("Zusage", "Accepted"); } },
  { key: "declined", get label() { return t("Abgesagt", "Declined"); } },
  { key: "lost", get label() { return t("Verloren", "Lost"); } },
  { key: "alle", get label() { return t("Alle", "All"); } },
];
export function passtFilter(z: AnfrageZeile, f: AnfrageFilter): boolean {
  if (f === "alle") return true;
  if (f === "offen") return z.offen;
  return z.inquiry.stage === f;
}
export function passtAuswahl(z: AnfrageZeile, a: { creator?: string; marke?: string; manager?: string; suche?: string }): boolean {
  if (a.creator && !z.candidates.some((c) => c.talent_id === a.creator)) return false;
  if (a.marke && z.inquiry.brand_id !== a.marke) return false;
  if (a.manager && (a.manager === "ohne" ? z.inquiry.manager_id !== null : z.inquiry.manager_id !== a.manager)) return false;
  const q = (a.suche ?? "").trim().toLowerCase();
  if (q && ![z.inquiry.title, z.brandName, ...z.creator, z.angebot?.number ?? ""].join(" ").toLowerCase().includes(q)) return false;
  return true;
}
export function zaehle(zeilen: readonly AnfrageZeile[]): Record<AnfrageFilter, number> {
  const out = Object.fromEntries(FILTERS.map((f) => [f.key, 0])) as Record<AnfrageFilter, number>;
  for (const z of zeilen) for (const f of FILTERS) if (passtFilter(z, f.key)) out[f.key]++;
  return out;
}
/** Board: eine Spalte je Stand (offene immer, abgeschlossene nur mit Einträgen) */
export function boardSpalten(zeilen: readonly AnfrageZeile[]): { stage: InquiryStage; pill: Pill; zeilen: AnfrageZeile[]; wert: Record<string, number> }[] {
  return STAGE_ORDER.map((stage) => {
    const list = zeilen.filter((z) => z.inquiry.stage === stage);
    const wert: Record<string, number> = {};
    for (const z of list) if (z.wertCents != null && z.angebot) wert[z.angebot.currency] = (wert[z.angebot.currency] ?? 0) + z.wertCents;
    return { stage, pill: STAGE_PILL[stage], zeilen: list, wert };
  }).filter((s) => isOpenStage(s.stage) || s.zeilen.length > 0);
}
/** Wert der offenen Angebote je Währung (letzte gesendete bzw. festgeschriebene Fassung) */
export function offenerWert(zeilen: readonly AnfrageZeile[]): Record<string, number> {
  return openOfferValue(zeilen.filter((z) => z.angebot).map((z) => ({ stage: z.inquiry.stage, currency: z.angebot!.currency, total_cents: z.wertCents })));
}

// ── Formular der Anfrage ────────────────────────────────────────────────────

export const NEW_BRAND = "__neu";
export interface AnfrageFormular {
  brandId: string;
  newBrand: string;
  contactId: string;
  title: string;
  source: string;
  receivedOn: string;
  managerId: string;
  campaignId: string;
  startOn: string;
  endOn: string;
  budget: string;
  currency: string;
  platforms: string[];
  usageMonths: string;
  exclusive: boolean;
  exclusiveUntil: string;
  exclusiveScope: string;
  rush: boolean;
  briefing: string;
  note: string;
  talents: string[];
}

export function leeresFormular(opts: { today: string; currency: string; userId: string | null; brandId?: string; talentId?: string }): AnfrageFormular {
  return {
    brandId: opts.brandId ?? "", newBrand: "", contactId: "", title: "", source: "email", receivedOn: opts.today, managerId: opts.userId ?? "", campaignId: "",
    startOn: "", endOn: "", budget: "", currency: opts.currency, platforms: [], usageMonths: "", exclusive: false, exclusiveUntil: "", exclusiveScope: "",
    rush: false, briefing: "", note: "", talents: opts.talentId ? [opts.talentId] : [],
  };
}

const text = (cents: number | null | undefined) => (cents == null ? "" : (cents / 100).toFixed(2).replace(".", ","));
export function formularAusAnfrage(i: InquiryRow, candidates: readonly CandidateRow[]): AnfrageFormular {
  return {
    brandId: i.brand_id, newBrand: "", contactId: i.contact_id ?? "", title: i.title, source: i.source ?? "", receivedOn: i.received_on, managerId: i.manager_id ?? "",
    campaignId: i.campaign_id ?? "", startOn: i.start_on ?? "", endOn: i.end_on ?? "", budget: text(i.budget_cents), currency: i.currency, platforms: [...i.platforms],
    usageMonths: i.usage_months == null ? "" : String(i.usage_months), exclusive: i.exclusive, exclusiveUntil: i.exclusive_until ?? "", exclusiveScope: i.exclusive_scope ?? "",
    rush: i.rush, briefing: i.briefing ?? "", note: i.note ?? "", talents: candidates.map((c) => c.talent_id),
  };
}

/** Betrag aus einer Eingabe („1.234,50“, „1234.5“, „1.100“, „1'100.50“) in Cent (parseAmountToCents); leer → null; ungültig → NaN */
export function centsOf(v: string): number | null {
  if (!v.trim()) return null;
  try {
    return parseAmountToCents(v);
  } catch {
    return Number.NaN;
  }
}
/** Prozent aus einer Eingabe („12,5“) in Basispunkten; leer → null; ungültig → NaN */
export function bpOf(v: string): number | null {
  const s = v.trim().replace(",", ".").replace(/\s*%$/, "");
  if (!s) return null;
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return Number.NaN;
  return Math.round(Number(s) * 100);
}
export const bpText = (bp: number | null | undefined) => (bp == null ? "" : String(bp / 100).replace(".", ","));

/**
 * Formular prüfen und die Werte für inquiry_save bauen. Ohne „Beträge sehen“ (betraege: false) bleibt das Budget der Marke
 * draußen: nicht geprüft, nicht gesendet; inquiry_save behält dann den gespeicherten Wert.
 */
export function pruefeFormular(f: AnfrageFormular, opts: { betraege?: boolean } = {}): { fehler: string[]; values: Record<string, unknown> | null; newBrand: string | null } {
  const betraege = opts.betraege ?? true;
  const fehler: string[] = [];
  if (!f.brandId) fehler.push(t("Bitte eine Marke wählen oder neu anlegen.", "Please choose a brand or create a new one."));
  if (f.brandId === NEW_BRAND && !f.newBrand.trim()) fehler.push(t("Bitte den Namen der neuen Marke eingeben.", "Please enter the name of the new brand."));
  if (!f.title.trim()) fehler.push(t("Bitte einen Titel eingeben (z. B. Kampagne oder Produkt).", "Please enter a title (e.g. campaign or product)."));
  if (f.title.trim().length > 120) fehler.push(t("Der Titel ist zu lang (höchstens 120 Zeichen).", "The title is too long (at most 120 characters)."));
  if (f.startOn && f.endOn && f.endOn < f.startOn) fehler.push(t("Das Ende liegt vor dem Beginn.", "The end is before the start."));
  const budget = betraege ? centsOf(f.budget) : null;
  if (budget !== null && (Number.isNaN(budget) || budget < 0)) fehler.push(t("Das Budget ist kein gültiger Betrag.", "The budget is not a valid amount."));
  const usage = f.usageMonths.trim() ? Number(f.usageMonths.trim()) : null;
  if (usage !== null && (!Number.isInteger(usage) || usage < 1 || usage > 120)) fehler.push(t("Nutzungsrechte: 1 bis 120 Monate.", "Usage rights: 1 to 120 months."));
  if (f.exclusive && f.exclusiveUntil && f.startOn && f.exclusiveUntil < f.startOn) fehler.push(t("Die Exklusivität endet vor dem Beginn.", "The exclusivity ends before the start."));
  if (fehler.length) return { fehler, values: null, newBrand: null };
  const values: Record<string, unknown> = {
    ...(f.brandId !== NEW_BRAND ? { brand_id: f.brandId, contact_id: f.contactId || null } : {}),
    title: f.title.trim(), source: f.source || null, received_on: f.receivedOn || null, manager_id: f.managerId || null, campaign_id: f.campaignId || null,
    start_on: f.startOn || null, end_on: f.endOn || null, ...(betraege ? { budget_cents: budget } : {}), currency: f.currency, platforms: f.platforms, usage_months: usage,
    exclusive: f.exclusive, exclusive_until: f.exclusive ? f.exclusiveUntil || null : null, exclusive_scope: f.exclusive ? f.exclusiveScope.trim() || null : null,
    rush: f.rush, briefing: f.briefing.trim() || null, note: f.note.trim() || null,
  };
  return { fehler, values, newBrand: f.brandId === NEW_BRAND ? f.newBrand.trim() : null };
}

// ── Exklusivität (vorhandene Konfliktprüfung aus packages/domain/src/kooperationen.ts) ──────────────────────────

export interface ExklusivDaten {
  exklusiv: readonly { id: string; talent_id: string; brand_id: string; title: string; status: string; archived_at: string | null; exclusive: boolean;
    exclusive_until: string | null; exclusive_scope: string | null; start_month: string | null; months: number | null }[];
  exklusivMonate: readonly { deal_id: string; month: string; status: string }[];
  brands: readonly { id: string; name: string }[];
  talents: readonly Talentlike[];
}

/**
 * Konflikte der Anfrage mit laufenden Kooperationen der vorgeschlagenen Creator, wie beim Anlegen einer Kooperation: die Anfrage
 * verlangt Exklusivität und der Creator arbeitet im Zeitraum für eine andere Marke, oder eine laufende Kooperation ist exklusiv
 * und diese Marke wäre eine andere. Ohne Zeitraum gilt der Tag des Eingangs.
 */
export function exklusivHinweise(a: { talents: readonly string[]; brandId: string; startOn: string; endOn: string; exclusive: boolean; exclusiveUntil: string; exclusiveScope: string;
  title: string; today: string }, d: ExklusivDaten): string[] {
  if (!a.brandId || a.brandId === NEW_BRAND || !a.talents.length) return [];
  const beginn = a.startOn || a.today;
  const ende = a.endOn && a.endOn >= beginn ? a.endOn : beginn;
  const monate = new Map<string, string[]>();
  for (const p of d.exklusivMonate) if (p.status !== "cancelled") monate.set(p.deal_id, [...(monate.get(p.deal_id) ?? []), p.month.slice(0, 7)]);
  const marke = (id: string) => d.brands.find((b) => b.id === id)?.name ?? "";
  const out: string[] = [];
  for (const talentId of a.talents) {
    const neu = { id: "__anfrage", talentId, brandId: a.brandId, title: a.title || t("Diese Anfrage", "This inquiry"), status: "planning", exclusive: a.exclusive,
      exclusiveUntil: a.exclusive ? a.exclusiveUntil || null : null, exclusiveScope: a.exclusive ? a.exclusiveScope.trim() || null : null, from: beginn, to: ende };
    const andere = d.exklusiv.filter((x) => x.talent_id === talentId).flatMap((x) => {
      const own = (monate.get(x.id) ?? []).sort();
      const s = own[0] ?? x.start_month?.slice(0, 7);
      if (!s) return [];
      return [{ id: x.id, talentId: x.talent_id, brandId: x.brand_id, title: x.title, status: x.status, archived: !!x.archived_at, exclusive: x.exclusive,
        exclusiveUntil: x.exclusive_until, exclusiveScope: x.exclusive_scope, from: `${s}-01`, to: lastDayOfMonth(own.at(-1) ?? addMonths(s, Math.max(1, x.months ?? 1) - 1)) }];
    });
    const name = nameOf(d.talents.find((t) => t.id === talentId));
    const titel = new Map(andere.map((x) => [x.id, x]));
    for (const c of exclusivityConflicts([neu, ...andere], marke, neu.id)) {
      const ex = c.exclusiveDealId === neu.id ? neu : titel.get(c.exclusiveDealId);
      const other = c.otherDealId === neu.id ? neu : titel.get(c.otherDealId);
      const bis = ex?.exclusiveUntil ? t(` bis ${ex.exclusiveUntil.split("-").reverse().join(".")}`, ` until ${ex.exclusiveUntil.split("-").reverse().join("/")}`) : "";
      out.push(c.exclusiveDealId === neu.id
        ? t(`${name}: Die Anfrage verlangt Exklusivität${bis}${c.scope ? ` (${c.scope})` : ""}; im selben Zeitraum läuft „${other?.title ?? "–"}“ mit ${marke(other?.brandId ?? "")}. Bitte prüfen, ob die Branche betroffen ist.`, `${name}: the inquiry requires exclusivity${bis}${c.scope ? ` (${c.scope})` : ""}; in the same period “${other?.title ?? "–"}” runs with ${marke(other?.brandId ?? "")}. Please check whether the industry is affected.`)
        : t(`${name}: ${marke(ex?.brandId ?? "")} · „${ex?.title ?? "–"}“ ist exklusiv${bis}${c.scope ? ` (${c.scope})` : ""}. ${c.named ? t(`${marke(a.brandId)} ist dort ausdrücklich genannt.`, `${marke(a.brandId)} is named there explicitly.`) : t("Bitte prüfen, ob diese Marke zur Branche gehört.", "Please check whether this brand belongs to the industry.")}`, `${name}: ${marke(ex?.brandId ?? "")} · “${ex?.title ?? "–"}” is exclusive${bis}${c.scope ? ` (${c.scope})` : ""}. ${c.named ? t(`${marke(a.brandId)} ist dort ausdrücklich genannt.`, `${marke(a.brandId)} is named there explicitly.`) : t("Bitte prüfen, ob diese Marke zur Branche gehört.", "Please check whether this brand belongs to the industry.")}`));
    }
  }
  return out;
}

// ── Angebotszeilen ──────────────────────────────────────────────────────────

let seq = 0;
export const zeilenKey = () => `z${++seq}`;

/** Eine Zeile im Angebotseditor: Eingaben als Text, Zuschläge einzeln (leer = kein Zuschlag) */
export interface ZeileEntwurf {
  key: string;
  candidateId: string;
  platform: string;
  format: string;
  formatLabel: string;
  title: string;
  note: string;
  quantity: string;
  price: string;
  usageBp: string;
  usageMonths: string;
  usagePerMonth: boolean;
  exclusivityBp: string;
  exclusivityMonths: string;
  exclusivityPerMonth: boolean;
  rushBp: string;
  revisionBp: string;
  revisions: string;
}

export function leereZeile(candidateId: string, platform = "instagram"): ZeileEntwurf {
  return {
    key: zeilenKey(), candidateId, platform, format: Object.entries(DELIVERABLE_FORMAT_PLATFORM).find(([, p]) => p === platform)?.[0] ?? "other", formatLabel: "", title: "", note: "",
    quantity: "1", price: "", usageBp: "", usageMonths: "", usagePerMonth: true, exclusivityBp: "", exclusivityMonths: "", exclusivityPerMonth: true, rushBp: "", revisionBp: "", revisions: "",
  };
}

export function zeileAusLine(l: Pick<OfferLineRow, "candidate_id" | "platform" | "format" | "format_label" | "title" | "note" | "quantity" | "unit_price_cents" | "surcharges">): ZeileEntwurf {
  const s = surchargesFromJson(l.surcharges);
  const get = (k: Surcharge["kind"]) => s.find((x) => x.kind === k);
  const u = get("usage"), e = get("exclusivity"), r = get("rush"), v = get("revision");
  return {
    key: zeilenKey(), candidateId: l.candidate_id, platform: l.platform, format: l.format, formatLabel: l.format_label ?? "", title: l.title ?? "", note: l.note ?? "",
    quantity: String(l.quantity), price: text(l.unit_price_cents),
    usageBp: bpText(u?.bp), usageMonths: u ? String(u.units) : "", usagePerMonth: u ? !!u.per_month : true,
    exclusivityBp: bpText(e?.bp), exclusivityMonths: e ? String(e.units) : "", exclusivityPerMonth: e ? !!e.per_month : true,
    rushBp: bpText(r?.bp), revisionBp: bpText(v?.bp), revisions: v ? String(v.units) : "",
  };
}

/** Zeile → Position für die Datenbank; Fehlertexte, wenn etwas nicht stimmt */
export function lineAusZeile(z: ZeileEntwurf, nr: number): { line: AngebotsZeile | null; fehler: string[] } {
  const fehler: string[] = [];
  const pre = t(`Position ${nr}`, `Item ${nr}`);
  // Gegenprüfung GB-4: ganze Zahl ohne Trenner (parseQuantityInput); „1.000“ ist unlesbar statt still 1, leer = 1
  const qty = parseQuantityInput(z.quantity, { integer: true }) ?? 1;
  if (!Number.isInteger(qty) || qty < 1 || qty > 999) fehler.push(t(`${pre}: Menge 1 bis 999.`, `${pre}: quantity 1 to 999.`));
  const price = centsOf(z.price);
  if (price === null || Number.isNaN(price) || price < 0) fehler.push(t(`${pre}: Bitte einen Preis angeben.`, `${pre}: please enter a price.`));
  if (z.format === "other" && !z.formatLabel.trim()) fehler.push(t(`${pre}: Bitte das Format benennen.`, `${pre}: please name the format.`));
  const surcharges: Surcharge[] = [];
  const add = (kind: Surcharge["kind"], bpIn: string, unitsIn: string, perMonth: boolean, label: string) => {
    const bp = bpOf(bpIn);
    if (bp === null) return;
    if (Number.isNaN(bp) || bp > 100000) { fehler.push(t(`${pre}: ${label} als Prozentsatz angeben (0 bis 1000).`, `${pre}: enter ${label} as a percentage (0 to 1000).`)); return; }
    if (bp === 0) return;
    // Gegenprüfung GB-4: Anzahl Monate bzw. Schleifen als ganze Zahl ohne Trenner (parseQuantityInput), unlesbar = Fehler
    const units = perMonth ? parseQuantityInput(unitsIn, { integer: true }) ?? 0 : 1;
    if (!Number.isInteger(units) || units < 1 || units > 120) { fehler.push(t(`${pre}: ${label}: Anzahl ${kind === "revision" ? t("Schleifen", "rounds") : t("Monate", "Months")} 1 bis 120.`, `${pre}: ${label}: number of ${kind === "revision" ? t("Schleifen", "rounds") : t("Monate", "Months")} 1 to 120.`)); return; }
    surcharges.push({ kind, bp, units, ...(perMonth && (kind === "usage" || kind === "exclusivity") ? { per_month: true } : {}) });
  };
  add("usage", z.usageBp, z.usageMonths, z.usagePerMonth, t("Nutzungsrechte", "Usage rights"));
  add("exclusivity", z.exclusivityBp, z.exclusivityMonths, z.exclusivityPerMonth, t("Exklusivität", "Exclusivity"));
  add("rush", z.rushBp, "1", false, t("Eilzuschlag", "Rush surcharge"));
  add("revision", z.revisionBp, z.revisions, true, t("Korrekturschleife", "Revision round"));
  if (fehler.length) return { line: null, fehler };
  return {
    line: {
      candidate_id: z.candidateId, platform: z.platform, format: z.format, format_label: z.format === "other" || z.formatLabel.trim() ? z.formatLabel.trim() || null : null,
      title: z.title.trim() || null, note: z.note.trim() || null, quantity: qty, unit_price_cents: price!, surcharges,
    },
    fehler,
  };
}

/** Betrag einer Zeile im Editor (ungültige Eingaben: null) */
export function zeilenBetrag(z: ZeileEntwurf): number | null {
  const r = lineAusZeile(z, 0);
  return r.line ? offerLineAmounts(r.line).totalCents : null;
}
export function editorSummen(zeilen: readonly ZeileEntwurf[], opts: { commissionIncluded: boolean; agencyFeeBp: number | null }) {
  const lines = zeilen.map((z, i) => lineAusZeile(z, i + 1).line).filter((l): l is AngebotsZeile => !!l);
  return offerTotals(lines, opts);
}

export function rateCardOf(c: RateCardRow): RateCard {
  return { currency: c.currency as Currency, usage_bp: c.usage_bp, usage_per_month: c.usage_per_month, exclusivity_bp: c.exclusivity_bp,
    exclusivity_per_month: c.exclusivity_per_month, rush_bp: c.rush_bp, revision_bp: c.revision_bp };
}

/** Wünsche der Anfrage für die Zuschläge: Nutzungsrechte in Monaten, Exklusivität bis …, Eile */
export function wunschAus(i: Pick<InquiryRow, "usage_months" | "exclusive" | "exclusive_until" | "start_on" | "end_on" | "received_on" | "rush">) {
  const from = i.start_on ?? i.received_on;
  return {
    usageMonths: i.usage_months,
    exclusivityMonths: i.exclusive ? monthsBetween(from, i.exclusive_until ?? i.end_on ?? from) : null,
    rush: i.rush,
  };
}

/**
 * Vorbelegung eines neuen Angebots aus den Preislisten: je offenem Creator die Preise der Plattformen der Anfrage (ohne Plattform
 * in der Anfrage alle Preise), Zuschläge nach den Wünschen der Anfrage. Andere Währung oder keine Preisliste: eine leere Zeile.
 */
export function vorbelegung(d: Pick<AnfrageDetail, "inquiry" | "candidates" | "cards" | "items">): { zeilen: ZeileEntwurf[]; hinweise: string[] } {
  const zeilen: ZeileEntwurf[] = [];
  const hinweise: string[] = [];
  const wunsch = wunschAus(d.inquiry);
  for (const c of d.candidates.filter((x) => x.status === "proposed")) {
    const card = d.cards.find((k) => k.talent_id === c.talent_id);
    const items = card ? d.items.filter((x) => x.card_id === card.id) : [];
    const passend = items.filter((x) => !d.inquiry.platforms.length || d.inquiry.platforms.includes(x.platform));
    if (!card || !passend.length || card.currency !== d.inquiry.currency) {
      if (card && card.currency !== d.inquiry.currency) hinweise.push(t(`Preisliste in ${card.currency}, Anfrage in ${d.inquiry.currency}: Preise bitte von Hand eintragen.`, `Price list in ${card.currency}, inquiry in ${d.inquiry.currency}: please enter prices by hand.`));
      zeilen.push(leereZeile(c.id, d.inquiry.platforms[0] ?? "instagram"));
      continue;
    }
    const sur = surchargesFromCard(rateCardOf(card), wunsch);
    for (const it of passend) {
      zeilen.push(zeileAusLine({ candidate_id: c.id, platform: it.platform, format: it.format, format_label: it.format_label, title: null, note: null, quantity: 1,
        unit_price_cents: it.price_cents, surcharges: sur }));
    }
  }
  return { zeilen, hinweise };
}

/** Preis aus der Preisliste für eine Zeile nachtragen (Knopf „Aus Preisliste“) */
export function preisAusListe(z: ZeileEntwurf, card: RateCardRow | undefined, items: readonly RateItemRow[], inquiry: InquiryRow): ZeileEntwurf | null {
  if (!card || card.currency !== inquiry.currency) return null;
  const it = items.find((x) => x.card_id === card.id && x.platform === z.platform && x.format === z.format && (x.format_label ?? "") === (z.format === "other" ? z.formatLabel.trim() : x.format_label ?? ""))
    ?? items.find((x) => x.card_id === card.id && x.platform === z.platform && x.format === z.format);
  if (!it) return null;
  const s = zeileAusLine({ candidate_id: z.candidateId, platform: z.platform, format: z.format, format_label: z.formatLabel || null, title: z.title || null, note: z.note || null,
    quantity: ((q) => (q !== null && Number.isInteger(q) && q >= 1 ? q : 1))(parseQuantityInput(z.quantity, { integer: true })),
    unit_price_cents: it.price_cents, surcharges: surchargesFromCard(rateCardOf(card), wunschAus(inquiry)) });
  // Die Menge bleibt, wie getippt (eine unlesbare meldet lineAusZeile, statt sie still auf 1 zu setzen)
  return { ...s, key: z.key, quantity: z.quantity };
}

export const FORMAT_CHOICES = DELIVERABLE_FORMATS;

// ── PDF ─────────────────────────────────────────────────────────────────────

const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);

/** Absätze aus einem Textfeld (Leerzeilen trennen) */
export function absaetze(t: string | null | undefined): string[] {
  return (t ?? "").split(/\n\s*\n/).map((x) => x.replace(/\s*\n\s*/g, " ").trim()).filter(Boolean);
}

/**
 * Modell des Angebots-PDF: festgeschriebene Angebote aus den eingefrorenen Parteien, Entwürfe aus den aktuellen Stammdaten.
 * Positionen je Creator (Casting: mit Überschrift), Beschreibung „Format · Plattform · Titel“, Hinweis zu Steuer und Provision.
 */
export function angebotPdfModell(a: {
  offer: OfferRow; lines: readonly OfferLineRow[]; candidates: readonly CandidateRow[]; inquiry: InquiryRow; talents: readonly Talentlike[];
  platformLabel: (key: string) => string; org: Record<string, unknown> | null; legal: Record<string, unknown> | null; brand: Record<string, unknown> | null;
  contactName: string | null; orgName: string; today: string; locale: MoneyLocale; logo?: OfferPdfModel["logo"];
}): OfferPdfModel {
  const lang: Language = a.offer.language === "en" ? "en" : "de";
  const st = a.org ?? {};
  const l = a.legal ?? {};
  const issuer: SnapshotParty = (a.offer.issuer_snapshot as SnapshotParty | null) ?? {
    name: str(st.legal_name) ?? a.orgName, street: str(st.street), postal_code: str(st.postal_code), city: str(st.city), country: str(st.country),
    vat_id: str(st.vat_id), tax_number: str(st.tax_number), email: str(st.reply_to_email), representative: str(l.representative_name),
    representative_position: str(l.representative_position), small_business: st.small_business === true ? true : null,
    register: registerText(str(l.register_type), str(l.register_number), str(l.register_court)), logo_path: str(st.logo_path), accent: str(st.brand_color),
  };
  const b = a.brand ?? {};
  const recipient = (a.offer.recipient_snapshot as (SnapshotParty & { contact?: string | null }) | null) ?? {
    name: str(b.legal_name) ?? str(b.name), street: str(b.street), postal_code: str(b.postal_code), city: str(b.city), country: str(b.country), vat_id: str(b.vat_id),
    contact: a.contactName,
  };
  const parties = offerParties(issuer, recipient, lang);
  const byCand = a.candidates.filter((c) => a.lines.some((x) => x.candidate_id === c.id));
  const groups = byCand.map((c) => ({
    heading: byCand.length > 1 ? nameOf(a.talents.find((t) => t.id === c.talent_id)) : null,
    lines: a.lines.filter((x) => x.candidate_id === c.id).sort((x, y) => x.sort_order - y.sort_order).map((x) => ({
      description: deliverableLabel({ format: x.format, format_label: x.format_label, platform: x.platform, title: x.title }, a.platformLabel, lang),
      detail: x.note, quantity: x.quantity, unitPriceCents: x.unit_price_cents, surcharges: surchargesFromJson(x.surcharges),
    })),
  }));
  // Ein Creator ohne Überschrift: der Name steht im Einleitungstext
  const single = byCand.length === 1 ? nameOf(a.talents.find((t) => t.id === byCand[0]!.talent_id)) : null;
  const intro = absaetze(a.offer.intro);
  if (single) intro.push(lang === "en" ? `Creator: ${single}` : `Creator: ${single}`);
  return {
    language: lang, locale: lang === "en" ? "en-GB" : a.locale, currency: a.offer.currency as Currency, number: a.offer.number,
    date: (a.offer.finalized_at ?? a.today).slice(0, 10), validUntil: a.offer.valid_until, sender: parties.sender, footerLines: footerLinesOf(issuer, lang),
    recipient: parties.recipient, title: a.inquiry.title, intro, groups, commissionIncluded: a.offer.commission_included, agencyFeeBp: a.offer.agency_fee_bp,
    basisText: priceBasisText({ basis: a.offer.prices_basis, commissionIncluded: a.offer.commission_included, agencyFeeBp: a.offer.agency_fee_bp,
      country: issuer.country ?? null, smallBusiness: !!issuer.small_business, lang }),
    terms: absaetze(a.offer.terms), accent: issuer.accent ?? null, logo: a.logo ?? null,
  };
}

/** Verlauf: Ereignis → Text */
export function ereignisText(e: { event: string; details: Record<string, unknown> }, ctx: { talentName: (id: string) => string; memberName: (id: string | null) => string | null }): string {
  const d = e.details ?? {};
  const who = (k = "talent_id") => (typeof d[k] === "string" ? ctx.talentName(d[k] as string) : "Creator");
  const stage = (k: string) => (typeof d[k] === "string" ? STAGE_PILL[d[k] as InquiryStage]?.label ?? String(d[k]) : "–");
  switch (e.event) {
    case "created": return t("Anfrage angelegt", "Inquiry created");
    case "candidate_added": return `${who()} vorgeschlagen`;
    case "candidate_removed": return t(`${who()} aus der Anfrage genommen`, `${who()} removed from the inquiry`);
    case "candidate_accepted": return t(`Zusage für ${who()} · Kooperation angelegt`, `Acceptance for ${who()} · deal created`);
    case "candidate_declined": return `${who()}: ${d.status === "lost" ? t("Marke hat abgesagt", "Brand declined") : t("abgesagt", "declined")}${d.reason ? ` · ${d.reason}` : ""}${d.note ? ` (${d.note})` : ""}`;
    case "candidate_reopened": return t(`${who()} wieder vorgeschlagen`, `${who()} proposed again`);
    case "offer_created": return t(`Angebot Fassung ${d.version ?? "?"} angelegt`, `Offer version ${d.version ?? "?"} created`);
    case "offer_finalized": return t(`Angebot ${d.number ?? ""} festgeschrieben`, `Offer ${d.number ?? ""} finalised`);
    case "offer_sent": return d.external ? t(`Angebot ${d.number ?? ""} als gesendet vermerkt (außerhalb des Portals)`, `Offer ${d.number ?? ""} marked as sent (outside the portal)`) : t(`Angebot ${d.number ?? ""} an ${d.recipient ?? "–"} gesendet`, `Offer ${d.number ?? ""} sent to ${d.recipient ?? "–"}`);
    case "stage": return t(`Stand: ${stage("from")} → ${stage("to")}${d.reason ? ` · ${d.reason}` : ""}`, `Status: ${stage("from")} → ${stage("to")}${d.reason ? ` · ${d.reason}` : ""}`);
    case "closed": return t(`Abgeschlossen: ${d.outcome === "lost" ? t("Verloren", "Lost") : t("Abgesagt", "Declined")} · ${d.reason ?? ""}${d.note ? ` (${d.note})` : ""}`, `Closed: ${d.outcome === "lost" ? t("Verloren", "Lost") : t("Abgesagt", "Declined")} · ${d.reason ?? ""}${d.note ? ` (${d.note})` : ""}`);
    case "reopened": return t("Wieder geöffnet", "Reopened");
    case "pitch_sent": return t(`Pitch-Liste „${d.title ?? "ohne Titel"}“ mit ${d.creators ?? "?"} Creator an ${d.recipient ?? "–"} angelegt`, `Pitch list “${d.title ?? "untitled"}” with ${d.creators ?? "?"} creators created for ${d.recipient ?? "–"}`);
    case "follow_up_due": return t(`Nachfassen fällig (${d.days ?? "?"} Tage ohne Antwort)`, `Follow-up due (${d.days ?? "?"} days without reply)`);
    default: return e.event;
  }
}

/** Zusage-Dialog: Vorschlag für Zeitraum und Honorar aus Anfrage und Angebot */
export function zusageVorschlag(i: InquiryRow, sumCents: number | null): { startMonth: string; months: number; budget: string } {
  const start = (i.start_on ?? i.received_on).slice(0, 7);
  const end = (i.end_on ?? i.start_on ?? i.received_on).slice(0, 7);
  const [y1, m1] = start.split("-").map(Number) as [number, number];
  const [y2, m2] = end.split("-").map(Number) as [number, number];
  const months = Math.max(1, Math.min(36, (y2 - y1) * 12 + (m2 - m1) + 1));
  return { startMonth: start, months, budget: text(sumCents) };
}

/** Herkunft einer Kooperation aus einer Zusage (PARITAET-15): Standard „Anfrage der Marke“, wählbar im Dialog, „none“ = keine Angabe */
export type ZusageHerkunft = DealOrigin | "none";

/**
 * Satz, der bei der Zusage wirklich greift (PARITAET-15), in derselben Reihenfolge wie app.freeze_deal_commission (0074):
 * Satz der gewählten Herkunft, sonst Staffel bzw. Satz des Creators, sonst Standard der Agentur. Der Dialog zeigt ihn als
 * Platzhalter, damit ein leeres Feld nicht einen anderen Satz festschreibt, als angezeigt wird.
 */
export function zusageSatz(a: { origin: ZusageHerkunft; originRates?: Partial<Record<DealOrigin, number>> | null; talentBp?: number | null;
  talentTiers?: unknown; defaultBp?: number | null }): { bp: number | null; label: string } {
  const pct = (bp: number) => `${String(bp / 100).replace(".", currentLang() === "en" ? "." : ",")} %`;
  const own = a.origin !== "none" ? a.originRates?.[a.origin] : undefined;
  if (a.origin !== "none" && typeof own === "number") {
    const name = ORIGIN_LABEL[a.origin][currentLang()];
    return { bp: own, label: t(`Satz für „${name}“: ${pct(own)}`, `Rate for “${name}”: ${pct(own)}`) };
  }
  if (Array.isArray(a.talentTiers) && a.talentTiers.length) return { bp: null, label: t("Staffel des Creators", "Creator's tiers") };
  if (typeof a.talentBp === "number") return { bp: a.talentBp, label: t(`Standard des Creators: ${pct(a.talentBp)}`, `Creator's default: ${pct(a.talentBp)}`) };
  if (typeof a.defaultBp === "number") return { bp: a.defaultBp, label: t(`Standard der Agentur: ${pct(a.defaultBp)}`, `Agency default: ${pct(a.defaultBp)}`) };
  return { bp: null, label: t("Standard", "Default") };
}
